/* global navigator, document, HTMLTextAreaElement */

/**
 * Safely copies text to clipboard with multiple fallback strategies
 * @param text - The text to copy to clipboard
 * @returns Promise that resolves when copy operation completes (success or failure)
 */
export const copyToClipboard = async (text: string): Promise<void> => {
  try {
    await tryModernClipboard(text);
  } catch (error) {
    tryLegacyClipboard(text);
  }
};

/**
 * Attempts to use the modern Clipboard API
 */
const tryModernClipboard = async (text: string): Promise<void> => {
  if (!navigator.clipboard?.writeText) {
    throw new Error("Modern clipboard API not available");
  }
  
  await navigator.clipboard.writeText(text);
};

/**
 * Fallback to legacy clipboard method using execCommand
 */
const tryLegacyClipboard = (text: string): void => {
  try {
    const textArea = createHiddenTextArea(text);
    document.body.appendChild(textArea);
    
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    
    document.body.removeChild(textArea);
  } catch (error) {
    // Silent fail for legacy clipboard
  }
};

/**
 * Creates a hidden textarea element for legacy clipboard operations
 */
const createHiddenTextArea = (text: string): HTMLTextAreaElement => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  textArea.style.opacity = '0';
  return textArea;
};
